import { animate, state, style, transition, trigger } from '@angular/animations';
import { ChangeDetectionStrategy, Component, DestroyRef, OnChanges, inject, input, output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { IdentityService } from 'src/app/core/identity.service';
import { CandidateNotes } from 'src/app/recruiting/candidates/new-profile/models/candidate-notes';
import { RecruitingService } from 'src/app/recruiting/recruiting.service';

@Component({
    selector: 'nova-v1-notes',
    templateUrl: './v1-notes.component.html',
    styleUrls: ['./v1-notes.component.scss'],
    animations: [
        trigger('balloonEffect', [
            state(
                'initial',
                style({
                    opacity: 0
                })
            ),
            state(
                'final',
                style({
                    opacity: 1
                })
            ),
            transition('initial=>final', animate('1500ms')),
            transition('final=>initial', animate('1500ms'))
        ])
    ],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [ReactiveFormsModule, MatFormFieldModule, MatInputModule, MatIconModule]
})
export class V1NotesComponent implements OnChanges {
    readonly candidateId = input.required<number>();

    recruiterId: number;

    readonly dataChanged = output<void>();

    travelNeedsSaveStatus = 'initial';
    jobDesiresSaveStatus = 'initial';

    travelNeeds = new UntypedFormControl();
    jobDesires = new UntypedFormControl();

    private travelNeedsTimeout: NodeJS.Timeout | null = null;
    private jobDesiresTimeout: NodeJS.Timeout | null = null;
    private readonly destroyRef = inject(DestroyRef);

    constructor(
        private readonly recruitingService: RecruitingService,
        private readonly identityService: IdentityService
    ) {
        this.recruiterId = identityService.current.contactId;
    }

    ngOnChanges(): void {
        this.recruitingService
            .getCandidateNotes(this.candidateId())
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((candidateNotes: CandidateNotes) => {
                this.travelNeeds.setValue(candidateNotes.travelNeeds);
                this.jobDesires.setValue(candidateNotes.jobDesires);
            });
    }

    saveTravelNeeds() {
        this.recruitingService
            .updateTravelNeeds(this.recruiterId, this.candidateId(), this.travelNeeds.value)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                this.travelNeedsSaveStatus = 'final';
                if (this.travelNeedsTimeout) {
                    clearTimeout(this.travelNeedsTimeout);
                }
                this.travelNeedsTimeout = setTimeout(() => {
                    this.travelNeedsSaveStatus = 'initial';
                    this.dataChanged.emit();
                }, 2000);
            });
    }

    saveJobDesires() {
        this.recruitingService
            .updateJobDesires(this.recruiterId, this.candidateId(), this.jobDesires.value)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                this.jobDesiresSaveStatus = 'final';
                if (this.jobDesiresTimeout) {
                    clearTimeout(this.jobDesiresTimeout);
                }
                this.jobDesiresTimeout = setTimeout(() => {
                    this.jobDesiresSaveStatus = 'initial';
                    this.dataChanged.emit();
                }, 2000);
            });
    }
}
